import { useState, useEffect, useCallback, Suspense, memo } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell3 } from '@/components/app-shell-3';
import { useQualificationsData } from '@/hooks/useQualificationsData';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Building2,
  Users,
  AlertCircle,
  BookOpen,
  GraduationCap,
  Clock,
  LayoutGrid,
  ChevronDown,
  CheckCircle,
  MessageCircle,
  User,
  Home,
  BarChart3,
  Eye,
  Boxes,
  Award,
  CalendarCheck,
  MessageSquare,
  GitBranch,
  FileText,
  ChevronRight,
  ShieldCheck
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { RolesTab } from './components/RolesTab';
import { RoleTemplateDialog } from './components/RoleTemplateDialog';
import { DeleteRoleTemplateDialog } from './components/DeleteRoleTemplateDialog';

// Lazy load dialogs for better initial load
const AddQualification = lazyWithRetry(() => import('@/components/modals/qualifications/AddQualification').then(m => ({ default: m.AddQualification })));
const EditQualification = lazyWithRetry(() => import('@/components/modals/qualifications/EditQualification').then(m => ({ default: m.EditQualification })));

// Global tools configuration - extracted to module level
const GLOBAL_TOOLS = [
  { id: 'spaces', name: 'Spaces', icon: Boxes, route: '/dashboard', color: '#6366f1' },
  { id: 'users', name: 'Users', icon: Users, route: '/company-team', color: '#10b981' },
  { id: 'qualifications', name: 'Quals & Roles', icon: Award, route: '/qualifications', color: '#f59e0b' },
  { id: 'trainers', name: 'Trainers', icon: GraduationCap, route: '/trainers', color: '#3b82f6' },
  { id: 'attendance', name: 'Attendance', icon: CalendarCheck, route: '/attendance', color: '#8b5cf6' },
  { id: 'posts', name: 'Posts', icon: MessageSquare, route: '/posts', color: '#ec4899' },
  { id: 'processes', name: 'Processes', icon: GitBranch, route: '/processes', color: '#14b8a6' },
];

// Helper function - extracted to module level
const getPriorityBadge = (priority) => {
  switch (priority) {
    case 'high':
      return <Badge variant='destructive'>High</Badge>;
    case 'medium':
      return <Badge variant='warning'>Medium</Badge>;
    case 'low':
      return <Badge variant='secondary'>Low</Badge>;
    default:
      return <Badge variant='outline'>{priority || 'None'}</Badge>;
  }
};

// Compliance progress bar component - extracted to module level
const ComplianceBar = memo(function ComplianceBar({ stats }) {
  const defaultStats = {
    totalAssigned: 0,
    valid: 0,
    validCrossSpace: 0,
    expiring: 0,
    expiringCrossSpace: 0,
    expired: 0,
    missing: 0,
    booked: 0,
    quoting: 0,
    required: 0,
    compliancePercentage: 0,
    isDocumentRequirement: false,
    ...stats
  };

  const validTotal = defaultStats.valid + (defaultStats.validCrossSpace || 0);
  const expiringTotal = defaultStats.expiring + (defaultStats.expiringCrossSpace || 0);
  const isDocReq = defaultStats.isDocumentRequirement || defaultStats.compliancePercentage === null;
  const compliancePercentage = isDocReq ? null : (defaultStats.compliancePercentage || 0);

  // Document requirements show N/A
  if (isDocReq) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full min-w-[120px] cursor-pointer">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-400">N/A</span>
                <span className="text-xs text-gray-400">Document</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-xs">
              <div className="font-semibold border-b pb-1 mb-1">Document Requirement</div>
              <p className="text-gray-500">This is a document requirement and is not included in compliance calculations.</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-full min-w-[120px] cursor-pointer">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">
                {compliancePercentage}%
              </span>
              <span className="text-xs text-gray-500">
                {validTotal + expiringTotal}/{defaultStats.required}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  compliancePercentage >= 80
                    ? 'bg-green-500'
                    : compliancePercentage >= 50
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${compliancePercentage}%` }}
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-xs">
            <div className="font-semibold border-b pb-1 mb-1">Compliance Breakdown</div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-green-400">
                <span className="inline-block w-3 text-center">✓</span>
                <span>Valid:</span>
              </span>
              <span className="font-medium">{validTotal}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-yellow-400">
                <span className="inline-block w-3 text-center">⚠</span>
                <span>Expiring:</span>
              </span>
              <span className="font-medium">{expiringTotal}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-red-400">
                <span className="inline-block w-3 text-center">✗</span>
                <span>Expired:</span>
              </span>
              <span className="font-medium">{defaultStats.expired}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-orange-400">
                <span className="inline-block w-3 text-center">○</span>
                <span>Missing:</span>
              </span>
              <span className="font-medium">{defaultStats.missing}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-blue-400">
                <span className="inline-block w-3 text-center">📅</span>
                <span>Booked:</span>
              </span>
              <span className="font-medium">{defaultStats.booked}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="flex items-center gap-1 text-purple-400">
                <span className="inline-block w-3 text-center">💬</span>
                <span>Quoting:</span>
              </span>
              <span className="font-medium">{defaultStats.quoting}</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

const RENEWAL_LABELS = {
  'none': 'No Renewal',
  '6_months': '6 Months',
  '1_year': '1 Year',
  '2_years': '2 Years',
  '3_years': '3 Years',
  '5_years': '5 Years',
};

// Qualification row component - extracted for proper memoization
const QualificationRow = memo(function QualificationRow({
  qual,
  stats,
  editingCell,
  editingValue,
  editingType,
  onStartEditing,
  onSaveInlineEdit,
  onCancelEditing,
  onEditingValueChange,
  onEditingTypeChange,
  onEdit,
  onDelete,
  onGapAnalysis,
  onNavigateToMatrix,
  onBuildQuote,
  hasEditPermission = false
}) {
  const isEditingPriority = editingCell?.qualId === qual.id && editingCell?.field === 'priority';
  const isEditingRequired = editingCell?.qualId === qual.id && editingCell?.field === 'calculated_required';
  const isEditingRenewal = editingCell?.qualId === qual.id && editingCell?.field === 'renewal_period';

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSaveInlineEdit(qual.id, field);
    } else if (e.key === 'Escape') {
      onCancelEditing();
    }
  };

  return (
    <TableRow>
      {/* Name Cell */}
      <TableCell>
        <div
          className='cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5 -mx-2 -my-1.5 transition-colors group'
          onClick={() => hasEditPermission ? onEdit(qual) : onNavigateToMatrix(qual)}
        >
          <div className='font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-sm' title={qual.name}>
            {qual.name}
          </div>
          {qual.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='text-sm text-muted-foreground line-clamp-1 overflow-hidden text-ellipsis max-w-md'>
                    {qual.description}
                  </div>
                </TooltipTrigger>
                <TooltipContent className='max-w-sm'>
                  <p>{qual.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>

      {/* Priority Cell */}
      <TableCell>
        {isEditingPriority && hasEditPermission ? (
          <Select
            value={editingValue}
            onValueChange={value => onSaveInlineEdit(qual.id, 'priority', value)}
            defaultOpen
          >
            <SelectTrigger className='h-8 w-full'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='high'>High</SelectItem>
              <SelectItem value='medium'>Medium</SelectItem>
              <SelectItem value='low'>Low</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div
            className={hasEditPermission ? 'cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1 transition-colors inline-block' : 'inline-block'}
            onClick={hasEditPermission ? () => onStartEditing(qual.id, 'priority', qual.priority) : undefined}
          >
            {getPriorityBadge(qual.priority)}
          </div>
        )}
      </TableCell>

      {/* Required Personnel Cell */}
      <TableCell>
        {isEditingRequired && hasEditPermission ? (
          <div className='flex items-center gap-1.5'>
            <Users className='h-3 w-3 text-gray-400' />
            <div className='relative flex items-center'>
              <Input
                type='number'
                value={editingValue}
                onChange={e => onEditingValueChange(e.target.value)}
                onKeyDown={e => handleKeyDown(e, 'calculated_required')}
                onBlur={() => onSaveInlineEdit(qual.id, 'calculated_required')}
                className='h-7 w-20 text-sm px-2 pr-6 border-blue-300 focus:border-blue-500 focus:ring-blue-500'
                placeholder='0'
                min='0'
                max={editingType === 'percentage' ? 100 : undefined}
                autoFocus
              />
              <button
                type='button'
                onMouseDown={e => {
                  e.preventDefault();
                  onEditingTypeChange(editingType === 'number' ? 'percentage' : 'number');
                }}
                className='absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded hover:bg-gray-100 transition-colors text-xs font-semibold text-gray-600 hover:text-gray-900'
                title={editingType === 'number' ? 'Switch to percentage' : 'Switch to number'}
              >
                {editingType === 'percentage' ? '%' : '#'}
              </button>
            </div>
            {editingType === 'percentage' && stats?.teamSize > 0 && editingValue > 0 && (
              <span className='text-xs text-gray-500 ml-1'>
                = <span className='font-medium text-gray-700'>{Math.ceil((editingValue / 100) * stats.teamSize)}</span>/{stats.teamSize}
              </span>
            )}
          </div>
        ) : (
          <div
            className={hasEditPermission ? 'flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1 transition-colors group' : 'flex items-center gap-1.5'}
            onClick={hasEditPermission ? () => onStartEditing(qual.id, 'calculated_required', qual.calculated_required) : undefined}
          >
            <Users className={hasEditPermission ? 'h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors' : 'h-3 w-3 text-gray-400'} />
            <span className={hasEditPermission ? 'text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors' : 'text-sm font-medium text-gray-700'}>
              {stats?.required || 'All'}
            </span>
          </div>
        )}
      </TableCell>

      {/* Renewal Period Cell */}
      <TableCell>
        {isEditingRenewal && hasEditPermission ? (
          <Select
            value={editingValue}
            onValueChange={value => onSaveInlineEdit(qual.id, 'renewal_period', value)}
            defaultOpen
          >
            <SelectTrigger className='h-8 w-[140px] border-blue-300 focus:border-blue-500 focus:ring-blue-500'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='none'>No Renewal</SelectItem>
              <SelectItem value='6_months'>6 Months</SelectItem>
              <SelectItem value='1_year'>1 Year</SelectItem>
              <SelectItem value='2_years'>2 Years</SelectItem>
              <SelectItem value='3_years'>3 Years</SelectItem>
              <SelectItem value='5_years'>5 Years</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div
            className={hasEditPermission ? 'cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1 transition-colors inline-flex items-center gap-1.5 group' : 'inline-flex items-center gap-1.5'}
            onClick={hasEditPermission ? () => onStartEditing(qual.id, 'renewal_period', qual.renewal_period) : undefined}
          >
            {qual.renewal_period ? (
              <>
                <Clock className={hasEditPermission ? 'h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors' : 'h-3 w-3 text-gray-400'} />
                <span className={hasEditPermission ? 'text-sm text-gray-700 group-hover:text-gray-900 transition-colors' : 'text-sm text-gray-700'}>
                  {RENEWAL_LABELS[qual.renewal_period] || qual.renewal_period}
                </span>
              </>
            ) : (
              <span className={hasEditPermission ? 'text-muted-foreground group-hover:text-gray-600 transition-colors' : 'text-muted-foreground'}>
                {hasEditPermission ? 'Click to set' : '-'}
              </span>
            )}
          </div>
        )}
      </TableCell>

      {/* Compliance Cell */}
      <TableCell>
        <ComplianceBar stats={stats} />
      </TableCell>

      {/* Quote Button Cell */}
      <TableCell>
        <Button
          variant='outline'
          size='sm'
          className='h-8'
          onClick={() => onBuildQuote(qual)}
        >
          <FileText className='h-3.5 w-3.5 mr-1.5' />
          Quote
        </Button>
      </TableCell>

      {/* Actions Cell */}
      <TableCell className='text-right'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <MoreVertical className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={() => onNavigateToMatrix(qual)}>
              <Eye className='h-4 w-4 mr-2' />
              View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onGapAnalysis(qual.id)}>
              <BarChart3 className='h-4 w-4 mr-2' />
              Gap Analysis
            </DropdownMenuItem>
            {hasEditPermission && (
              <>
                <DropdownMenuItem onClick={() => onEdit(qual)}>
                  <Edit className='h-4 w-4 mr-2' />
                  Edit Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(qual)} className='text-red-600'>
                  <Trash2 className='h-4 w-4 mr-2' />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
});

// Stats card component - compact inline style
const StatCard = memo(function StatCard({ title, value, icon: Icon, iconColor = 'text-blue-500', className = '' }) {
  return (
    <div className='bg-white p-4 rounded-lg border'>
      <div className='flex items-center justify-between'>
        <div>
          <p className='text-sm text-gray-500'>{title}</p>
          <p className={`text-2xl font-bold ${className}`}>{value}</p>
        </div>
        {Icon && <Icon className={`h-8 w-8 ${iconColor} opacity-50`} />}
      </div>
    </div>
  );
});

// Global tools dropdown component
const GlobalToolsDropdown = memo(function GlobalToolsDropdown({ currentTool, navigate }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='h-8 px-2 flex items-center gap-1 border border-gray-200 bg-background hover:bg-muted data-[state=open]:bg-muted data-[state=open]:border-gray-300 rounded-md'
        >
          <LayoutGrid className='h-4 w-4 text-gray-600' />
          <ChevronDown className='h-3 w-3 text-gray-500 data-[state=open]:rotate-180 transition-transform' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        sideOffset={8}
        className='w-[400px] p-4 rounded-lg border border-gray-200 shadow-lg bg-white'
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className='grid grid-cols-3 gap-3'>
          {GLOBAL_TOOLS.map((tool) => {
            const Icon = tool.icon;
            const isActive = tool.id === currentTool;

            if (!Icon) return null;
            const isValidComponent = typeof Icon === 'function' || (typeof Icon === 'object' && Icon.$$typeof);
            if (!isValidComponent) return null;

            return (
              <div
                key={tool.id}
                className={`relative flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  isActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={(e) => {
                  if (tool.id !== currentTool) {
                    if (e.ctrlKey || e.metaKey) {
                      window.open(tool.route, '_blank');
                    } else {
                      navigate(tool.route);
                    }
                  }
                }}
                onAuxClick={(e) => {
                  if (e.button === 1 && tool.id !== currentTool) {
                    e.preventDefault();
                    window.open(tool.route, '_blank');
                  }
                }}
              >
                <div
                  className='w-12 h-12 rounded-lg flex items-center justify-center mb-2 flex-shrink-0'
                  style={{ backgroundColor: tool.color + '20' }}
                >
                  <Icon className='h-6 w-6' style={{ color: tool.color }} />
                </div>
                <div className='h-8 flex items-center justify-center'>
                  <span className='text-xs font-medium text-center line-clamp-2 w-full leading-tight'>
                    {tool.name}
                  </span>
                </div>
                {isActive && (
                  <div className='absolute top-2 right-2'>
                    <CheckCircle className='h-4 w-4 text-blue-600' />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

// Gap analysis dialog component
const GapAnalysisDialog = memo(function GapAnalysisDialog({ qual, stats, onClose }) {
  if (!qual) return null;

  const qualStats = stats || {
    totalAssigned: 0,
    valid: 0,
    validCrossSpace: 0,
    expiring: 0,
    expiringCrossSpace: 0,
    expired: 0,
    missing: 0,
    booked: 0,
    quoting: 0,
    required: 0,
    compliancePercentage: 0
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{qual.name} - GAP Analysis</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-4">
          {qual.description && (
            <p className="text-sm text-gray-600 pb-3 border-b">{qual.description}</p>
          )}

          {/* Compliance Overview */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600">Overall Compliance</span>
              <span className={`text-2xl font-bold ${
                qualStats.compliancePercentage >= 80 ? 'text-green-600' :
                qualStats.compliancePercentage >= 50 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {qualStats.compliancePercentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  qualStats.compliancePercentage >= 80 ? 'bg-green-500' :
                  qualStats.compliancePercentage >= 50 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${Math.min(100, qualStats.compliancePercentage)}%` }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-green-50 rounded-lg p-2.5 border border-green-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-green-700 font-medium">Valid</span>
                <span className="text-lg font-bold text-green-600">{qualStats.valid + (qualStats.validCrossSpace || 0)}</span>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-2.5 border border-yellow-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-yellow-700 font-medium">Expiring</span>
                <span className="text-lg font-bold text-yellow-600">{qualStats.expiring + (qualStats.expiringCrossSpace || 0)}</span>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-red-700 font-medium">Expired</span>
                <span className="text-lg font-bold text-red-600">{qualStats.expired}</span>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-orange-700 font-medium">Missing</span>
                <span className="text-lg font-bold text-orange-600">{qualStats.missing}</span>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-700 font-medium">Booked</span>
                <span className="text-lg font-bold text-blue-600">{qualStats.booked}</span>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-purple-700 font-medium">Quoting</span>
                <span className="text-lg font-bold text-purple-600">{qualStats.quoting}</span>
              </div>
            </div>
          </div>

          {/* Total Summary */}
          <div className="border-t pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Required</span>
              <span className="text-xl font-bold text-blue-600">{qualStats.required}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Total Assigned</span>
              <span className="text-xl font-bold text-gray-900">{qualStats.totalAssigned}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

// Card-based tab switcher component
const CardTabSwitcher = memo(function CardTabSwitcher({ activeTab, onTabChange }) {
  const tabs = [
    { id: 'qualifications', icon: Award, color: '#f59e0b', title: 'Qualifications', description: 'Training requirements across spaces' },
    { id: 'roles', icon: ShieldCheck, color: '#6366f1', title: 'Role Templates', description: 'Reusable sets of qualifications' },
  ];

  return (
    <div className="flex items-center justify-center gap-4">
      {/* Tab group */}
      <div className="flex rounded-lg border bg-white overflow-hidden shadow-xs">
        {tabs.map((tab, i) => {
          const Icon = tab.icon;
          const isActive = tab.id === activeTab;
          return (
            <button key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                'flex items-center gap-3 px-5 py-3 transition-colors text-left',
                i > 0 && 'border-l',
                isActive ? 'bg-blue-50/60' : 'hover:bg-gray-50'
              )}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: isActive ? tab.color + '20' : '#f3f4f6' }}>
                <Icon className="h-5 w-5" style={{ color: isActive ? tab.color : '#9ca3af' }} />
              </div>
              <div>
                <div className={cn('text-sm font-semibold', isActive ? 'text-gray-900' : 'text-gray-600')}>
                  {tab.title}
                </div>
                <div className="text-xs text-gray-500">{tab.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export function QualificationsPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { userProfile, canEditTraining } = useAuth();
  usePageTitle('Qualifications & Roles');

  // Check if user has permission to edit training/qualifications
  const hasEditPermission = canEditTraining();

  // Active tab state
  const [activeTab, setActiveTab] = useState('qualifications');

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState(projectId || 'all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [rolesSearchQuery, setRolesSearchQuery] = useState('');

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [openGapAnalysisId, setOpenGapAnalysisId] = useState(null);

  // Role template dialog state
  const [isRoleTemplateDialogOpen, setIsRoleTemplateDialogOpen] = useState(false);
  const [isDeleteRoleTemplateDialogOpen, setIsDeleteRoleTemplateDialogOpen] = useState(false);
  const [selectedRoleTemplate, setSelectedRoleTemplate] = useState(null);

  // Role templates refresh key - increment to force RolesTab to re-fetch
  const [rolesRefreshKey, setRolesRefreshKey] = useState(0);
  const refetchRoleTemplates = useCallback(() => {
    setRolesRefreshKey(k => k + 1);
  }, []);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingType, setEditingType] = useState('number');

  // Accordion state for collapse/expand all
  const [expandedItems, setExpandedItems] = useState([]);
  const [hasInitializedAccordion, setHasInitializedAccordion] = useState(false);

  // Use optimized data hook - single RPC call instead of N+1
  const {
    qualifications,
    qualificationsByProject,
    projects,
    summary,
    loading,
    refetch
  } = useQualificationsData(userProfile?.company_id, {
    projectId: filterProject !== 'all' ? filterProject : null,
    search: searchQuery,
    priority: filterPriority,
    enabled: !!userProfile?.company_id
  });

  // Build stats map from qualifications data (stats are embedded from RPC)
  const qualStatsMap = new Map();
  qualifications.forEach(qual => {
    if (qual.stats) {
      qualStatsMap.set(qual.id, qual.stats);
    }
  });

  // Update filterProject when URL projectId changes
  useEffect(() => {
    if (projectId) {
      setFilterProject(projectId);
    }
  }, [projectId]);

  // Initialize expanded items when data loads (only once)
  useEffect(() => {
    if (!hasInitializedAccordion && Object.keys(qualificationsByProject).length > 0) {
      setExpandedItems(Object.keys(qualificationsByProject));
      setHasInitializedAccordion(true);
    }
  }, [qualificationsByProject, hasInitializedAccordion]);

  // Toggle all accordions
  const toggleAllAccordions = useCallback(() => {
    const allKeys = Object.keys(qualificationsByProject);
    if (expandedItems.length === allKeys.length) {
      setExpandedItems([]);
    } else {
      setExpandedItems(allKeys);
    }
  }, [qualificationsByProject, expandedItems]);

  const allExpanded = expandedItems.length === Object.keys(qualificationsByProject).length;

  // Handlers wrapped with useCallback
  const handleAdd = useCallback(() => {
    setIsAddDialogOpen(true);
  }, []);

  const handleEdit = useCallback((qualification) => {
    setSelectedQualification(qualification);
    setIsEditDialogOpen(true);
  }, []);

  const handleDelete = useCallback((qualification) => {
    setSelectedQualification(qualification);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedQualification) return;

    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .delete()
        .eq('id', selectedQualification.id);

      if (error) throw error;

      toast.success('Qualification deleted successfully');
      setIsDeleteDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error deleting qualification:', error);
      toast.error('Failed to delete qualification');
    }
  }, [selectedQualification, refetch]);

  const handleGapAnalysis = useCallback((qualId) => {
    setOpenGapAnalysisId(qualId);
  }, []);

  const handleNavigateToMatrix = useCallback((qual) => {
    navigate(`/space/${qual.project_id}/matrix`, {
      state: {
        viewMode: 'qualifications',
        qualificationId: qual.id
      }
    });
  }, [navigate]);

  const handleBuildQuote = useCallback((qual) => {
    navigate(`/space/${qual.project_id}/build-quote?course=${encodeURIComponent(qual.name)}&from=${encodeURIComponent(window.location.pathname)}`);
  }, [navigate]);

  // Inline editing handlers
  const startEditing = useCallback((qualId, field, currentValue) => {
    setEditingCell({ qualId, field });

    if (field === 'calculated_required') {
      const qualification = qualifications.find(q => q.id === qualId);
      const minimumPersonnel = qualification?.minimum_personnel;

      if (minimumPersonnel && typeof minimumPersonnel === 'string' && minimumPersonnel.includes('%')) {
        setEditingType('percentage');
      } else {
        setEditingType('number');
      }
      setEditingValue(currentValue || '');
    } else {
      setEditingValue(currentValue || '');
    }
  }, [qualifications]);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditingValue('');
    setEditingType('number');
  }, []);

  const saveInlineEdit = useCallback(async (qualId, field, directValue = null) => {
    try {
      const valueToSave = directValue !== null ? directValue : editingValue;

      let updateData = {
        updated_date: new Date().toISOString()
      };

      if (field === 'calculated_required') {
        if (valueToSave === '' || valueToSave === null) {
          updateData.calculated_required = null;
          updateData.minimum_personnel = null;
        } else {
          updateData.calculated_required = parseInt(valueToSave);
          updateData.minimum_personnel = editingType === 'percentage' ? `${valueToSave}%` : String(valueToSave);
        }
      } else {
        updateData[field] = valueToSave;
      }

      const { error } = await supabase
        .from('compliance_requirements')
        .update(updateData)
        .eq('id', qualId);

      if (error) throw error;

      await refetch();
      toast.success('Updated successfully');
      cancelEditing();
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to update');
    }
  }, [editingValue, editingType, refetch, cancelEditing]);

  const handleEditingValueChange = useCallback((value) => {
    setEditingValue(value);
  }, []);

  const handleEditingTypeChange = useCallback((type) => {
    setEditingType(type);
  }, []);

  // Get qualification for gap analysis dialog
  const gapAnalysisQual = openGapAnalysisId ? qualifications.find(q => q.id === openGapAnalysisId) : null;
  const gapAnalysisStats = openGapAnalysisId ? qualStatsMap.get(openGapAnalysisId) : null;

  return (
    <AppShell3>
      {/* Subnav */}
      <div className='w-full bg-white border-b border-gray-200'>
        <div className='flex items-center justify-between py-3 px-6'>
          {/* Left side - Tool Switcher + Breadcrumb */}
          <div className='flex items-center gap-3'>
            <GlobalToolsDropdown currentTool="qualifications" navigate={navigate} />

            {/* Breadcrumb */}
            <div className='flex items-center gap-2'>
              <button
                onClick={() => navigate('/dashboard')}
                className='text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors'
              >
                Dashboard
              </button>
              <span className='text-sm text-gray-300'>/</span>
              <h1 className='text-sm font-semibold text-gray-700'>Qualifications & Roles</h1>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className='flex items-center gap-3'>
            {activeTab === 'qualifications' ? (
              <>
                {/* Search */}
                <div className='relative w-64'>
                  <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search qualifications...'
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className='pl-9 h-8'
                  />
                </div>

                {/* Filters */}
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className='w-[180px] h-8'>
                    <SelectValue placeholder='All Spaces' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Spaces</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className='w-[150px] h-8'>
                    <SelectValue placeholder='All Priorities' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All Priorities</SelectItem>
                    <SelectItem value='high'>High</SelectItem>
                    <SelectItem value='medium'>Medium</SelectItem>
                    <SelectItem value='low'>Low</SelectItem>
                  </SelectContent>
                </Select>

                {/* Add Button - Only show if user has permission */}
                {hasEditPermission && (
                  <Button onClick={handleAdd} size='sm' className='flex items-center gap-2 h-8'>
                    <Plus className='h-4 w-4' />
                    Add Qualification
                  </Button>
                )}
              </>
            ) : (
              <>
                {/* Roles tab actions */}
                <div className='relative w-64'>
                  <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search role templates...'
                    value={rolesSearchQuery}
                    onChange={e => setRolesSearchQuery(e.target.value)}
                    className='pl-9 h-8'
                  />
                </div>

                {hasEditPermission && (
                  <Button
                    onClick={() => {
                      setSelectedRoleTemplate(null);
                      setIsRoleTemplateDialogOpen(true);
                    }}
                    size='sm'
                    className='flex items-center gap-2 h-8'
                  >
                    <Plus className='h-4 w-4' />
                    Add Role Template
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8 py-6'>

        {/* Tab Switcher */}
        <CardTabSwitcher activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Tab Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsContent value="qualifications" className="space-y-6 mt-0 w-full">

        {/* Statistics Cards */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <StatCard title="Total Qualifications" value={summary.totalQualifications} icon={Award} iconColor="text-amber-500" />
          <StatCard title="Active Spaces" value={summary.activeProjects} icon={Building2} iconColor="text-purple-500" />
          <StatCard title="High Priority" value={summary.highPriority} icon={AlertCircle} iconColor="text-red-500" className="text-red-600" />
          <StatCard title="Categories" value={summary.categories} icon={BookOpen} iconColor="text-blue-500" />
        </div>

        {/* Qualifications Table */}
        {loading ? (
          <div className='flex items-center justify-center h-64'>
            <LoadingSpinner />
          </div>
        ) : Object.keys(qualificationsByProject).length === 0 ? (
          <Card>
            <CardContent className='text-center py-12'>
              <BookOpen className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
              <p className='text-muted-foreground mb-4'>
                {searchQuery ||
                filterProject !== 'all' ||
                filterPriority !== 'all'
                  ? 'No qualifications found matching your filters'
                  : hasEditPermission
                    ? 'No qualifications added yet'
                    : 'No qualifications have been added yet'}
              </p>
              {!searchQuery &&
                filterProject === 'all' &&
                filterPriority === 'all' &&
                hasEditPermission && (
                  <Button onClick={handleAdd}>
                    <Plus className='h-4 w-4 mr-2' />
                    Add First Qualification
                  </Button>
                )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Collapse/Expand All Button */}
            <div className="flex justify-end mb-3">
              <button
                onClick={toggleAllAccordions}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            <div className='space-y-4'>
            {Object.entries(qualificationsByProject).map(
              ([projectName, projectQuals]) => {
                const isExpanded = expandedItems.includes(projectName);
                return (
                <div key={projectName} className="rounded-xl bg-white shadow-xs ring-1 ring-gray-200 overflow-hidden">
                  {/* Space header */}
                  <button
                    onClick={() => {
                      setExpandedItems(prev =>
                        prev.includes(projectName)
                          ? prev.filter(i => i !== projectName)
                          : [...prev, projectName]
                      );
                    }}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-left"
                  >
                    <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    <Building2 className='h-4 w-4 text-gray-400' />
                    <span className='text-sm font-semibold text-gray-900'>{projectName}</span>
                    <Badge variant='secondary' className="text-xs">{projectQuals.length}</Badge>
                  </button>

                  {/* Table content */}
                  {isExpanded && (
                    <div className="border-t">
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[30%]">Name</TableHead>
                          <TableHead className="w-[10%]">Priority</TableHead>
                          <TableHead className="w-[10%]">Required</TableHead>
                          <TableHead className="w-[12%]">Validity</TableHead>
                          <TableHead className="w-[18%]">Compliance</TableHead>
                          <TableHead className="w-[12%]"></TableHead>
                          <TableHead className="w-[8%] text-right"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {projectQuals.map(qual => (
                          <QualificationRow
                            key={qual.id}
                            qual={qual}
                            stats={qualStatsMap.get(qual.id)}
                            editingCell={editingCell}
                            editingValue={editingValue}
                            editingType={editingType}
                            onStartEditing={startEditing}
                            onSaveInlineEdit={saveInlineEdit}
                            onCancelEditing={cancelEditing}
                            onEditingValueChange={handleEditingValueChange}
                            onEditingTypeChange={handleEditingTypeChange}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onGapAnalysis={handleGapAnalysis}
                            onNavigateToMatrix={handleNavigateToMatrix}
                            onBuildQuote={handleBuildQuote}
                            hasEditPermission={hasEditPermission}
                          />
                        ))}

                        {/* Totals Row - Only show when viewing a specific space */}
                        {filterProject !== 'all' && projectQuals.length > 0 && (() => {
                          const totals = projectQuals.reduce((acc, qual) => {
                            const stats = qualStatsMap.get(qual.id) || {
                              totalAssigned: 0,
                              valid: 0,
                              validCrossSpace: 0,
                              expiring: 0,
                              expiringCrossSpace: 0,
                              expired: 0,
                              missing: 0,
                              booked: 0,
                              quoting: 0,
                              required: 0,
                              compliancePercentage: 0,
                              isDocumentRequirement: false
                            };

                            const isDocReq = stats.isDocumentRequirement || stats.compliancePercentage === null;

                            return {
                              required: acc.required + (isDocReq ? 0 : (stats.required || 0)),
                              assigned: acc.assigned + stats.totalAssigned,
                              valid: acc.valid + (stats.valid || 0) + (stats.validCrossSpace || 0),
                              expiring: acc.expiring + (stats.expiring || 0) + (stats.expiringCrossSpace || 0),
                              expired: acc.expired + (stats.expired || 0),
                              missing: acc.missing + (stats.missing || 0),
                              booked: acc.booked + (stats.booked || 0),
                              quoting: acc.quoting + (stats.quoting || 0),
                            };
                          }, {
                            required: 0,
                            assigned: 0,
                            valid: 0,
                            expiring: 0,
                            expired: 0,
                            missing: 0,
                            booked: 0,
                            quoting: 0,
                          });

                          let totalCompliancePercentage = 0;
                          let qualificationCount = 0;

                          projectQuals.forEach(qual => {
                            const stats = qualStatsMap.get(qual.id);
                            if (stats) {
                              if (stats.isDocumentRequirement || stats.compliancePercentage === null) {
                                return;
                              }
                              totalCompliancePercentage += stats.compliancePercentage || 0;
                              qualificationCount++;
                            }
                          });

                          const overallCompliance = qualificationCount > 0
                            ? Math.round(totalCompliancePercentage / qualificationCount)
                            : 0;

                          return (
                            <TableRow className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                              <TableCell className="text-gray-900">
                                TOTALS
                              </TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>
                                <div className="text-sm text-gray-700">
                                  {totals.required}
                                </div>
                              </TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="w-full min-w-[120px] cursor-pointer">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs font-semibold text-gray-700">
                                            {overallCompliance}%
                                          </span>
                                          <span className="text-xs text-gray-600">
                                            {totals.valid}/{totals.required}
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
                                          <div
                                            className={`h-full rounded-full transition-all duration-300 ${
                                              overallCompliance >= 80
                                                ? 'bg-green-600'
                                                : overallCompliance >= 50
                                                ? 'bg-yellow-600'
                                                : 'bg-red-600'
                                            }`}
                                            style={{ width: `${overallCompliance}%` }}
                                          />
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <div className="space-y-1 text-xs">
                                        <div className="font-semibold border-b pb-1 mb-1">Overall Totals</div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-green-400">
                                            <span className="inline-block w-3 text-center">✓</span>
                                            <span>Valid:</span>
                                          </span>
                                          <span className="font-medium">{totals.valid}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-yellow-400">
                                            <span className="inline-block w-3 text-center">⚠</span>
                                            <span>Expiring:</span>
                                          </span>
                                          <span className="font-medium">{totals.expiring}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-red-400">
                                            <span className="inline-block w-3 text-center">✗</span>
                                            <span>Expired:</span>
                                          </span>
                                          <span className="font-medium">{totals.expired}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-orange-400">
                                            <span className="inline-block w-3 text-center">○</span>
                                            <span>Missing:</span>
                                          </span>
                                          <span className="font-medium">{totals.missing}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-blue-400">
                                            <span className="inline-block w-3 text-center">📅</span>
                                            <span>Booked:</span>
                                          </span>
                                          <span className="font-medium">{totals.booked}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="flex items-center gap-1 text-purple-400">
                                            <span className="inline-block w-3 text-center">💬</span>
                                            <span>Quoting:</span>
                                          </span>
                                          <span className="font-medium">{totals.quoting}</span>
                                        </div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </TableCell>
                              <TableCell>-</TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                    </div>
                  )}
                </div>
              );
              }
            )}
            </div>
          </>
        )}

          </TabsContent>

          <TabsContent value="roles" className="mt-0 w-full">
            <RolesTab
              key={rolesRefreshKey}
              searchQuery={rolesSearchQuery}
              onEdit={(template) => {
                setSelectedRoleTemplate(template);
                setIsRoleTemplateDialogOpen(true);
              }}
              onDelete={(template) => {
                setSelectedRoleTemplate(template);
                setIsDeleteRoleTemplateDialogOpen(true);
              }}
            />
          </TabsContent>
        </Tabs>

      </div>

      {/* Add Dialog - Lazy loaded */}
      <Suspense fallback={null}>
        {isAddDialogOpen && (
          <AddQualification
            isOpen={isAddDialogOpen}
            onClose={() => setIsAddDialogOpen(false)}
            projects={projects}
            onSuccess={refetch}
          />
        )}
      </Suspense>

      {/* Edit Dialog - Lazy loaded */}
      <Suspense fallback={null}>
        {isEditDialogOpen && (
          <EditQualification
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            selectedQualification={selectedQualification}
            onSuccess={refetch}
            teamMemberCount={selectedQualification ? (qualStatsMap.get(selectedQualification.id)?.teamSize || 0) : 0}
          />
        )}
      </Suspense>

      {/* Gap Analysis Dialog */}
      {openGapAnalysisId && (
        <GapAnalysisDialog
          qual={gapAnalysisQual}
          stats={gapAnalysisStats}
          onClose={() => setOpenGapAnalysisId(null)}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <DialogTitle className="text-center">Delete Qualification</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to delete <strong>{selectedQualification?.name}</strong>?
            </DialogDescription>
          </DialogHeader>

          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              This will remove it from the project and any associated assignments. This action cannot be undone.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleConfirmDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Qualification
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Template Create/Edit Dialog */}
      <RoleTemplateDialog
        isOpen={isRoleTemplateDialogOpen}
        onClose={() => {
          setIsRoleTemplateDialogOpen(false);
          setSelectedRoleTemplate(null);
        }}
        template={selectedRoleTemplate}
        onSuccess={refetchRoleTemplates}
      />

      {/* Role Template Delete Dialog */}
      <DeleteRoleTemplateDialog
        isOpen={isDeleteRoleTemplateDialogOpen}
        onClose={() => {
          setIsDeleteRoleTemplateDialogOpen(false);
          setSelectedRoleTemplate(null);
        }}
        template={selectedRoleTemplate}
        onSuccess={refetchRoleTemplates}
      />
    </AppShell3>
  );
}
